"use client"

import { useCallback, useEffect, useRef, useState } from "react"

import {
  kayitDogrula,
  kayitDurumu,
  kayitTalebiOlustur,
} from "../services/kayit-api"
import type { KayitDurum, KayitTalebiRequest } from "../types/kayit-types"

export type KayitAdim = "form" | "otp" | "hazirlaniyor" | "kilitli"

/** Polling aralığı — doküman §1'in önerisi. */
const DURUM_POLL_ARALIGI_MS = 2000
/** Bu süreyi aşan HAZIRLANIYOR'da sonsuz spinner yerine destek uyarısı gösterilir (§7). */
const KURULUM_UZADI_ESIGI_MS = 5 * 60 * 1000

/** Sayfa yenilense de polling sürsün diye; URL'e konmaz (§7: talep_id opak kullanılır). */
const TALEP_STORAGE_KEY = "muhasebe.kayit.talep"

type SaklananTalep = { talepId: number; tenantCode?: string }

function talebiOku(): SaklananTalep | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.sessionStorage.getItem(TALEP_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SaklananTalep
    return typeof parsed?.talepId === "number" ? parsed : null
  } catch {
    return null
  }
}

function talebiYaz(talep: SaklananTalep | null) {
  if (typeof window === "undefined") return
  try {
    if (talep) {
      window.sessionStorage.setItem(TALEP_STORAGE_KEY, JSON.stringify(talep))
    } else {
      window.sessionStorage.removeItem(TALEP_STORAGE_KEY)
    }
  } catch {
    // Gizli sekme / kapalı depolama: akış bellek üstünden yürür.
  }
}

export interface KayitFlow {
  adim: KayitAdim
  isSubmitting: boolean
  hataMesaji: string | null
  /** Yanlış OTP sonrası kalan hak; `null` ise henüz yanlış denenmemiş. */
  kalanDeneme: number | null
  tenantCode: string | null
  durum: KayitDurum | null
  kurulumUzadi: boolean
  email: string | null
  talepOlustur: (values: KayitTalebiRequest) => Promise<void>
  otpDogrula: (otp: string) => Promise<void>
  hataTemizle: () => void
  yenidenBasla: () => void
}

/**
 * §1 — kayıt akışının tamamı: talep → OTP → şema kurulumu polling'i.
 *
 * Public rotada React Query yok (`(public)/layout.tsx` provider mount etmiyor),
 * bu yüzden polling `setTimeout` zinciriyle yürür.
 */
export function useKayitFlow(): KayitFlow {
  const [adim, setAdim] = useState<KayitAdim>("form")
  const [talepId, setTalepId] = useState<number | null>(null)
  const [tenantCode, setTenantCode] = useState<string | null>(null)
  const [durum, setDurum] = useState<KayitDurum | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [hataMesaji, setHataMesaji] = useState<string | null>(null)
  const [kalanDeneme, setKalanDeneme] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [kurulumUzadi, setKurulumUzadi] = useState(false)

  const pollTimerRef = useRef<number | null>(null)
  const pollBaslangicRef = useRef<number | null>(null)

  const pollDurdur = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  // Sayfa yenilendiyse yarım kalan talebi devral.
  useEffect(() => {
    const saklanan = talebiOku()
    if (!saklanan) return
    setTalepId(saklanan.talepId)
    setTenantCode(saklanan.tenantCode ?? null)
    setAdim(saklanan.tenantCode ? "hazirlaniyor" : "otp")
  }, [])

  // Kurulum durumu polling'i.
  useEffect(() => {
    if (adim !== "hazirlaniyor" || talepId === null) return

    let iptal = false
    pollBaslangicRef.current ??= Date.now()

    const tur = async () => {
      try {
        const sonuc = await kayitDurumu(talepId)
        if (iptal) return

        if (!sonuc) {
          // Talep silinmiş/bulunamıyor: polling'i durdur, yeni talep önerisi göster.
          talebiYaz(null)
          setAdim("kilitli")
          setHataMesaji("Kayıt talebi bulunamadı. Lütfen yeni bir kayıt oluşturun.")
          return
        }

        setDurum(sonuc.durum)
        if (sonuc.tenant_code) setTenantCode(sonuc.tenant_code)

        if (sonuc.durum === "AKTIF") {
          talebiYaz(null)
          return
        }
        if (sonuc.durum === "KILITLI") {
          talebiYaz(null)
          setAdim("kilitli")
          setHataMesaji("Kayıt talebi kilitlendi. Lütfen yeni bir kayıt oluşturun.")
          return
        }

        const gecen = Date.now() - (pollBaslangicRef.current ?? Date.now())
        if (gecen >= KURULUM_UZADI_ESIGI_MS) setKurulumUzadi(true)

        pollTimerRef.current = window.setTimeout(tur, DURUM_POLL_ARALIGI_MS)
      } catch {
        if (iptal) return
        // Geçici ağ hatasında akışı öldürme; bir sonraki turda yeniden dener.
        pollTimerRef.current = window.setTimeout(tur, DURUM_POLL_ARALIGI_MS)
      }
    }

    void tur()

    return () => {
      iptal = true
      pollDurdur()
    }
  }, [adim, talepId, pollDurdur])

  const talepOlustur = useCallback(async (values: KayitTalebiRequest) => {
    setIsSubmitting(true)
    setHataMesaji(null)
    try {
      const sonuc = await kayitTalebiOlustur(values)

      if (!sonuc.ok) {
        // 429 DRF throttle biçiminde `detail` döner; diğerleri `mesaj` taşır.
        setHataMesaji(
          sonuc.hata.mesaj ??
            sonuc.hata.detail ??
            "Kayıt talebi oluşturulamadı. Lütfen bilgilerinizi kontrol edin.",
        )
        return
      }

      setTalepId(sonuc.data.talep_id)
      setEmail(values.email)
      setKalanDeneme(null)
      talebiYaz({ talepId: sonuc.data.talep_id })
      setAdim("otp")
    } catch {
      setHataMesaji("Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.")
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  const otpDogrula = useCallback(
    async (otp: string) => {
      if (talepId === null) return

      setIsSubmitting(true)
      setHataMesaji(null)
      try {
        const sonuc = await kayitDogrula({ talep_id: talepId, otp })

        if (sonuc.ok) {
          setTenantCode(sonuc.data.tenant_code)
          setDurum(sonuc.data.durum)
          talebiYaz({ talepId, tenantCode: sonuc.data.tenant_code })
          setAdim("hazirlaniyor")
          return
        }

        const { hata, status } = sonuc

        // Çift tık: hata değil, talep zaten doğrulanmış → kurulum ekranına geç.
        if (hata.hata === "zaten_tamamlanmis" && hata.tenant_code) {
          setTenantCode(hata.tenant_code)
          talebiYaz({ talepId, tenantCode: hata.tenant_code })
          setAdim("hazirlaniyor")
          return
        }

        // Talep silinmişse (404) sessionStorage'daki id ölüdür; yeni talebe yönlendir.
        if (status === 404) {
          talebiYaz(null)
          setAdim("kilitli")
          setHataMesaji("Kayıt talebi bulunamadı. Lütfen yeni bir kayıt oluşturun.")
          return
        }

        if (status === 403 || hata.hata === "talep_kilitli") {
          talebiYaz(null)
          setAdim("kilitli")
          setHataMesaji(
            hata.mesaj ?? "Çok fazla hatalı deneme yapıldı; yeni kayıt oluşturun.",
          )
          return
        }

        if (hata.hata === "otp_yanlis") {
          const kalan = hata.kalan_deneme ?? 0
          setKalanDeneme(kalan)
          if (kalan <= 0) {
            talebiYaz(null)
            setAdim("kilitli")
            setHataMesaji("Çok fazla hatalı deneme yapıldı; yeni kayıt oluşturun.")
            return
          }
          setHataMesaji(`Kod hatalı. Kalan deneme hakkınız: ${kalan}.`)
          return
        }

        if (hata.hata === "otp_suresi_doldu") {
          talebiYaz(null)
          setAdim("kilitli")
          setHataMesaji(
            hata.mesaj ?? "Kodun süresi doldu; yeni kayıt talebi oluşturun.",
          )
          return
        }

        setHataMesaji(hata.mesaj ?? "Kod doğrulanamadı. Lütfen tekrar deneyin.")
      } catch {
        setHataMesaji("Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.")
      } finally {
        setIsSubmitting(false)
      }
    },
    [talepId],
  )

  const hataTemizle = useCallback(() => setHataMesaji(null), [])

  const yenidenBasla = useCallback(() => {
    pollDurdur()
    pollBaslangicRef.current = null
    talebiYaz(null)
    setAdim("form")
    setTalepId(null)
    setTenantCode(null)
    setDurum(null)
    setEmail(null)
    setHataMesaji(null)
    setKalanDeneme(null)
    setKurulumUzadi(false)
  }, [pollDurdur])

  return {
    adim,
    isSubmitting,
    hataMesaji,
    kalanDeneme,
    tenantCode,
    durum,
    kurulumUzadi,
    email,
    talepOlustur,
    otpDogrula,
    hataTemizle,
    yenidenBasla,
  }
}
